'use client';

import { motion } from 'motion/react';
import { FaChrome, FaStar } from 'react-icons/fa6';
import Image from '@/components/Image';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/pengi-live-tutor/lkjhjffmaaiaikckielejcaefdpbdhkg';


const AVATARS = [
  { emoji: '👦', bg: '#FFF0E0' },
  { emoji: '👧', bg: '#EBF3FF' },
  { emoji: '🧒', bg: '#F3EEFF' },
  { emoji: '👦🏽', bg: '#E8FBE4' },
  { emoji: '👧🏻', bg: '#FDEEFF' },
];

export default function ScratchHeroSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 0%, #FFF0E0 0%, #FFF8F0 30%, #FAFCFF 100%)',
      }}
    >
      {/* Decorative grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #FFD6A0 1px, transparent 1px), linear-gradient(to bottom, #FFD6A0 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(ellipse 70% 50% at 50% 30%, black 0%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 50% at 50% 30%, black 0%, transparent 100%)',
        }}
      />

      {/* Ambient glow blobs */}
      <div
        className="pointer-events-none absolute -left-32 top-0 h-[420px] w-[420px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, #FF8C1A 0%, transparent 70%)',
          filter: 'blur(90px)',
        }}
      />
      <div
        className="pointer-events-none absolute -right-24 top-10 h-[320px] w-[320px] rounded-full opacity-15"
        style={{
          background: 'radial-gradient(circle, #4C97FF 0%, transparent 70%)',
          filter: 'blur(90px)',
        }}
      />


      <div className="relative mx-auto max-w-[1200px] px-4 pb-10 pt-16 md:px-8 md:pb-20 md:pt-24">
        <div className="flex flex-col items-center gap-10 lg:flex-row lg:gap-14">
          {/* ── Left: text ── */}
          <div className="flex-1 lg:max-w-[520px]">
            <motion.div
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#FFD6A0] bg-white/80 px-4 py-1.5 shadow-sm backdrop-blur-sm"
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <FaChrome className="h-4 w-4 text-[#FF8C1A]" />
              <span className="display-text-semi text-xs tracking-wide text-[#CC6600]">
                Chrome Extension for Scratch
              </span>
            </motion.div>

            <motion.h1
              className="emphasize-text text-[36px] leading-[1.08] tracking-[-0.04em] text-[#0D1929] md:text-[52px]"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              Your kid is stuck on Scratch.
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    'linear-gradient(135deg, #FF8C1A 0%, #E8740C 60%, #CC5500 100%)',
                }}
              >
                Help is one click away.
              </span>
            </motion.h1>

            <motion.p
              className="display-text mt-5 max-w-[440px] text-base leading-[1.7] text-[#4A5A72] md:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.55 }}
            >
              Pengi sits{' '}
              <strong className="text-[#0D1929]">inside the Scratch editor</strong> and
              talks your child through every bug — with voice, in real time.
            </motion.p>

            <motion.div
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.55 }}
            >
              <div className="inline-block self-start">
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-ph-capture-attribute-button="add-to-chrome-hero"
                  data-ph-capture-attribute-page="scratch"
                  className="display-text-bold group inline-flex items-center gap-2.5 rounded-full bg-[#0D1929] px-7 py-3.5 text-base text-white shadow-[0_4px_24px_rgba(13,25,41,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(13,25,41,0.4)]"
                >
                  <FaChrome className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180" />
                  Add to Chrome
                </a>
              </div>
              <span className="display-text text-xs text-[#B0B8C4]">Free trial included</span>
            </motion.div>

            {/* Social proof */}
            <motion.div
              className="mt-8 flex items-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.56, duration: 0.55 }}
            >
              <div className="flex -space-x-2">
                {AVATARS.map((a, i) => (
                  <div
                    key={i}
                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-sm shadow-sm"
                    style={{ background: a.bg }}
                  >
                    {a.emoji}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <FaStar key={s} className="h-3 w-3 text-[#FF8C1A]" />
                  ))}
                </div>
                <p className="display-text text-[11px] text-[#8A96A8]">
                  Loved by 1,000+ students
                </p>
              </div>
            </motion.div>
          </div>

          {/* ── Right: Browser mockup ── */}
          <motion.div
            className="w-full max-w-[640px] flex-1"
            initial={{ opacity: 0, x: 32, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
            >
              <div className="overflow-hidden rounded-2xl border border-[#D8E3EF] shadow-[0_8px_48px_rgba(31,42,68,0.12)]">
                <Image
                  src="assets/scratch-editor-screenshot.png"
                  alt="Pengi Live Tutor running inside the Scratch editor"
                  width={1400}
                  height={860}
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <p className="display-text-semi mt-4 text-center text-sm tracking-wide text-[#8A96A8]">
              Sees the blocks. Hears the question. Gives help, NOT answers.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
